/**
 * A link to a URL an editor wrote.
 *
 * Two jobs, and both were missing everywhere a section rendered a bare `<a href>`:
 *
 * 1. **The language prefix.** See lib/links.ts - without it every internal link bounced
 *    off the router and back to the homepage.
 * 2. **Client-side navigation.** An internal link is a react-router `Link`, so following
 *    one does not reload the bundle, the chrome and the fonts.
 *
 * An external link stays an `<a>`, opens in a new tab and carries `rel="noreferrer"`.
 * `mailto:`, `tel:` and `#anchor` are left exactly as written - a new tab for a mail
 * client is wrong, and an anchor belongs to the page it is on.
 */
import { Link } from 'react-router-dom';
import type { AnchorHTMLAttributes, ReactNode } from 'react';
import { isExternalUrl, resolveHref } from '../../lib/links';
import { useSite } from '../../lib/site-context';

type Props = {
  to?: string | null;
  children: ReactNode;
} & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'children'>;

export function AppLink({ to, children, ...rest }: Props) {
  const { locale } = useSite();
  const url = (to ?? '').trim();
  if (!url) return <>{children}</>;

  const href = resolveHref(locale, url);

  if (isExternalUrl(url)) {
    const newTab = /^https?:/i.test(url) || url.startsWith('//');
    return (
      <a href={href} {...(newTab ? { target: '_blank', rel: 'noreferrer' } : {})} {...rest}>
        {children}
      </a>
    );
  }

  return <Link to={href} {...rest}>{children}</Link>;
}
